import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { saveAs } from 'file-saver';

import useCore from 'hooks/useCore';
import { getAnnotationPopupPositionBasedOn } from 'helpers/getPopupPosition';
import applyRedactions from 'helpers/applyRedactions';
import { isMobile, isIE } from 'helpers/device';
import { useTranslation } from 'react-i18next';
import { getOpenedWarningModal, getOpenedColorPicker, getDatePicker } from 'helpers/getElements';
import getGroupedLinkAnnotations from 'helpers/getGroupedLinkAnnotations';
import getHashParameters from 'helpers/getHashParameters';
import useOnClickOutside from 'hooks/useOnClickOutside';
import actions from 'actions';
import selectors from 'selectors';
import DataElements from 'constants/dataElement';
import { PRIORITY_TWO, PRIORITY_THREE } from 'constants/actionPriority';
import getRootNode from 'helpers/getRootNode';
import { ITEM_RENDER_PREFIXES } from 'constants/customizationVariables';
import AnnotationPopup from './AnnotationPopup';

import './AnnotationPopup.scss';
import { isAnnotationRenderedInDisplayMode } from 'src/helpers/isAnnotationRenderedInDisplayMode';

const { ToolNames } = window.Core.Tools;
const { Annotations } = window.Core;

const propTypes = {
  focusedAnnotation: PropTypes.object,
  selectedMultipleAnnotations: PropTypes.bool,
  canModify: PropTypes.bool,
  focusedAnnotationStyle: PropTypes.object,
  isStylePopupOpen: PropTypes.bool,
  setIsStylePopupOpen: PropTypes.func,
  isDatePickerOpen: PropTypes.bool,
  setDatePickerOpen: PropTypes.func,
  isDatePickerMount: PropTypes.bool,
  setDatePickerMount: PropTypes.func,
  hasAssociatedLink: PropTypes.bool,
  includesFormFieldAnnotation: PropTypes.bool,
  stylePopupRepositionFlag: PropTypes.bool,
  setStylePopupRepositionFlag: PropTypes.func,
  closePopup: PropTypes.func,
  widgetThatOpenedPopupRef: PropTypes.object,
};

const AnnotationPopupContainer = ({
  focusedAnnotation,
  selectedMultipleAnnotations,
  canModify,
  focusedAnnotationStyle,
  isStylePopupOpen,
  setIsStylePopupOpen,
  isDatePickerOpen,
  setDatePickerOpen,
  isDatePickerMount,
  setDatePickerMount,
  hasAssociatedLink,
  includesFormFieldAnnotation,
  stylePopupRepositionFlag,
  setStylePopupRepositionFlag,
  closePopup,
  widgetThatOpenedPopupRef,
}) => {
  const { core } = useCore();
  const isDisabled = useSelector((state) => selectors.isElementDisabled(state, DataElements.ANNOTATION_POPUP));
  const isOpen = useSelector((state) => selectors.isElementOpen(state, DataElements.ANNOTATION_POPUP));
  const isContextMenuPopupOpen = useSelector((state) => selectors.isElementOpen(state, DataElements.CONTEXT_MENU_POPUP));
  const isRightClickAnnotationPopupEnabled = useSelector(selectors.isRightClickAnnotationPopupEnabled);
  const isNotesPanelDisabled = useSelector((state) => selectors.isElementDisabled(state, DataElements.NOTES_PANEL));
  const isAnnotationStylePopupDisabled = useSelector((state) => selectors.isElementDisabled(state, DataElements.ANNOTATION_STYLE_POPUP));
  const isInlineCommentingDisabled = useSelector((state) => selectors.isElementDisabled(state, DataElements.INLINE_COMMENT_POPUP));
  const isNotesPanelOpen = useSelector((state) => selectors.isElementOpen(state, DataElements.NOTES_PANEL));
  const isLinkModalOpen = useSelector((state) => selectors.isElementOpen(state, DataElements.LINK_MODAL));
  const isWarningModalOpen = useSelector((state) => selectors.isElementOpen(state, DataElements.WARNING_MODAL));
  const isRichTextPopupOpen = useSelector((state) => selectors.isElementOpen(state, 'richTextPopup'));
  const isMultiTab = useSelector(selectors.getIsMultiTab);
  const tabManager = useSelector(selectors.getTabManager);
  const tabs = useSelector(selectors.getTabs);
  const notesInLeftPanel = useSelector(selectors.getNotesInLeftPanel);
  const leftPanelOpen = useSelector((state) => selectors.isElementOpen(state, DataElements.LEFT_PANEL));
  const activeLeftPanel = useSelector(selectors.getActiveLeftPanel);
  const activeDocumentViewerKey = useSelector(selectors.getActiveDocumentViewerKey);
  const isAnyCustomPanelOpen = useSelector(selectors.isAnyCustomPanelOpen);
  const featureFlags = useSelector(selectors.getFeatureFlags);
  const isStylePanelOpen = useSelector((state) => selectors.isElementOpen(state, DataElements.STYLE_PANEL));
  const isStylePanelDisabled = useSelector((state) => selectors.isElementDisabled(state, DataElements.STYLE_PANEL));
  const stylePanelInFlyout = useSelector((state) => selectors.getIsPanelInFlyout(state, ITEM_RENDER_PREFIXES.STYLE_PANEL, [DataElements.MULTI_SELECT_STYLE_PANEL_FLYOUT]));
  const isStylePanelFlyoutOpen = useSelector((state) => selectors.isElementOpen(state, stylePanelInFlyout?.dataElement));
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isCalibrationPopupOpen, setCalibrationPopupOpen] = useState(false);
  const popupRef = useRef();

  const isFocusedAnnotationSelected = isRightClickAnnotationPopupEnabled ? core.isAnnotationSelected(focusedAnnotation, activeDocumentViewerKey) : true;
  const annotManager = core.getAnnotationManager(activeDocumentViewerKey);
  const sixtyFramesPerSecondIncrement = 16;
  // on tablet, the behaviour will be like on desktop, including being draggable

  const { customizableUI } = featureFlags;

  const toggleStylePanel = () => {
    if (!isStylePanelOpen && !isStylePanelDisabled && !stylePanelInFlyout) {
      dispatch(actions.openElement(DataElements.STYLE_PANEL));
    }
    if (stylePanelInFlyout) {
      if (isStylePanelFlyoutOpen) {
        dispatch(actions.closeElement(stylePanelInFlyout.dataElement));
      } else {
        dispatch(actions.openFlyout(stylePanelInFlyout.dataElement, 'annotationStyleEditButton'));
      }
    }
    if (!stylePanelInFlyout) {
      closePopup();
    }
  };

  useOnClickOutside(
    popupRef,
    useCallback((e) => {
      const notesPanel = getRootNode().querySelector(`[data-element="${DataElements.NOTES_PANEL}"]`);
      const clickedInNotesPanel = notesPanel?.contains(e.target);
      const clickedInLinkModal = getRootNode().querySelector('.LinkModal.open')?.contains(e.target);
      const stylePanelFlyout = getRootNode().querySelector(`[data-element=${stylePanelInFlyout?.dataElement}]`);
      const clickedInStylePanelFlyout = stylePanelFlyout?.contains(e.target);
      const datePicker = getDatePicker();
      const warningModal = getOpenedWarningModal();
      const colorPicker = getOpenedColorPicker();

      // the notes panel has mousedown handlers to handle the opening/closing states of this component
      // we don't want this handler to run when clicked in the notes panel otherwise the opening/closing states may mess up
      // for example: click on a note will call core.selectAnnotation which triggers the annotationSelected event
      // and opens this component. If we don't exclude the notes panel this handler will run and close it after
      // Similarly, we don't want the popup to close when clicking on the style panel flyout or the flyout loses its anchor.
      if (!clickedInNotesPanel && !clickedInLinkModal && !clickedInStylePanelFlyout && !warningModal && !colorPicker && !datePicker) {
        if (isRightClickAnnotationPopupEnabled) {
          closePopup();
        } else {
          dispatch(actions.closeElement(DataElements.ANNOTATION_POPUP));
        }
      }
    }, [isRightClickAnnotationPopupEnabled])
  );

  const setPopupPosition = () => {
    const visiblePages = core.getDisplayModeObject().getVisiblePages();
    const isFocusableInCurrentDisplayMode = isAnnotationRenderedInDisplayMode(core, focusedAnnotation);
    if (popupRef.current && isFocusableInCurrentDisplayMode && focusedAnnotation) {
      setPosition(getAnnotationPopupPositionBasedOn(focusedAnnotation, popupRef, activeDocumentViewerKey));
    } else if (!isNotesPanelDisabled && !isFocusableInCurrentDisplayMode) {
      const annotations = core.getSelectedAnnotations();
      const annotationInPage = annotations.find(
        (annotation) => visiblePages.includes(annotation.PageNumber)
      );
      setPosition(getAnnotationPopupPositionBasedOn(annotationInPage, popupRef, activeDocumentViewerKey));
    }
  };

  const handleResize = debounce(() => {
    if (AnnotationPopupContainer) {
      setPopupPosition();
    }
  }, sixtyFramesPerSecondIncrement, { 'trailing': true, 'leading': false });

  const handleVisibility = debounce(() => {
    if (AnnotationPopupContainer) {
      setIsVisible(isFocusedAnnotationSelected);
    }
  }, sixtyFramesPerSecondIncrement * 2, { 'trailing': true, 'leading': false });

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // useLayoutEffect here to avoid flashing issue when popup is close and open on scroll
  useLayoutEffect(() => {
    if (focusedAnnotation || isStylePopupOpen || isDatePickerMount) {
      handleResize();
      setIsVisible(false);
      handleVisibility();
    }
    // canModify is needed here because the effect from useOnAnnotationPopupOpen hook will run again and determine which button to show, which in turn change the popup size and will need to recalculate position
  }, [focusedAnnotation, isStylePopupOpen, isDatePickerMount, canModify, activeDocumentViewerKey]);

  useEffect(() => {
    if (focusedAnnotation && focusedAnnotation.ToolName === ToolNames.CALIBRATION_MEASUREMENT) {
      setCalibrationPopupOpen(true);
    }
  }, [focusedAnnotation]);

  useEffect(() => {
    if (isOpen || isRichTextPopupOpen) {
      dispatch(actions.closeElement(DataElements.INLINE_COMMENT_POPUP));
    }
  }, [isOpen, isRichTextPopupOpen]);

  /* VIEW FILE */
  const wvServer = !!getHashParameters('webviewerServerURL', null);
  const acceptFormats = wvServer ? window.Core.SupportedFileFormats.SERVER : window.Core.SupportedFileFormats.CLIENT;
  const showViewFileButton = focusedAnnotation instanceof Annotations.FileAttachmentAnnotation && isMultiTab
    && acceptFormats.includes(window.Core.mimeTypeToExtension[focusedAnnotation.getFileMetadata().mimeType]);

  const onViewFile = useCallback(async () => {
    if (!tabManager || !isMultiTab) {
      return console.warn('Can\'t open file in non-multi-tab mode');
    }
    const metaData = focusedAnnotation.getFileMetadata();
    const fileAttachmentTab = tabs.find((tab) => tab.options.filename === metaData.filename);
    if (fileAttachmentTab) { // If already opened once
      await tabManager.setActiveTab(fileAttachmentTab.id, true);
      return;
    }
    await tabManager.addTab(await focusedAnnotation.getFileData(), {
      extension: window.Core.mimeTypeToExtension[metaData.mimeType],
      filename: metaData.filename,
      saveCurrentActiveTabState: true,
      setActive: true,
    });
  }, [tabManager, focusedAnnotation, tabs, isMultiTab]);

  useEffect(() => {
    const popup = popupRef.current;
    if (!popup) {
      return;
    }
    if (widgetThatOpenedPopupRef?.current) {
      const associatedSignatureAnnotation = widgetThatOpenedPopupRef.current.getAssociatedSignatureAnnotation();
      const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
          event.preventDefault();
          event.stopPropagation();
          if (associatedSignatureAnnotation === focusedAnnotation) {
            annotManager.deselectAnnotation(focusedAnnotation, activeDocumentViewerKey);
          }
          closePopup();
        }
        if (canModify && (event.key === 'Delete' || event.key === 'Backspace') && isOpen) {
          event.preventDefault();
          event.stopPropagation();
          if (widgetThatOpenedPopupRef?.current?.isSignedByAppearance()) {
            widgetThatOpenedPopupRef.current.clearSignature(annotManager);
          } else if (associatedSignatureAnnotation) {
            annotManager.deleteAnnotation(associatedSignatureAnnotation, activeDocumentViewerKey);
          }
          closePopup();
        }
      };

      popup.addEventListener('keydown', handleKeyDown, true);
      return () => popup.removeEventListener('keydown', handleKeyDown, true);
    }
  }, [popupRef,isOpen, widgetThatOpenedPopupRef, focusedAnnotation, closePopup, annotManager, activeDocumentViewerKey]);

  /* ALL REACT HOOKS NEED TO BE BEFORE RENDERING */
  if (isDisabled || !focusedAnnotation) {
    return null;
  }

  /* COMMENT / DATE */
  const selectedAnnotations = core.getSelectedAnnotations(activeDocumentViewerKey);
  const numberOfSelectedAnnotations = selectedAnnotations.length;
  const multipleAnnotationsSelected = numberOfSelectedAnnotations > 1;
  const isFreeTextAnnot = focusedAnnotation instanceof Annotations.FreeTextAnnotation;
  const isDateFreeTextCanEdit = isFreeTextAnnot && !!focusedAnnotation.getDateFormat() && core.canModifyContents(focusedAnnotation, activeDocumentViewerKey);
  const numberOfGroups = core.getNumberOfGroups(selectedAnnotations, activeDocumentViewerKey);
  const canGroup = numberOfGroups > 1;
  const canUngroup = numberOfGroups === 1 && numberOfSelectedAnnotations > 1 && isFocusedAnnotationSelected;
  const isAppearanceSignature =
    focusedAnnotation instanceof Annotations.SignatureWidgetAnnotation
    && focusedAnnotation.isSignedByAppearance();
  const isInReadOnlyMode = core.getIsReadOnly();

  const showCommentButton = (
    (!isNotesPanelDisabled || !isInlineCommentingDisabled)
    && (!multipleAnnotationsSelected || (multipleAnnotationsSelected && !isFocusedAnnotationSelected))
    && focusedAnnotation.ToolName !== ToolNames.CROP
    && !includesFormFieldAnnotation
    && !focusedAnnotation.isContentEditPlaceholder()
    && !isAppearanceSignature
  );

  const onCommentAnnotation = () => {
    if (isDateFreeTextCanEdit) {
      setDatePickerOpen(true);
      return;
    }

    dispatch(actions.closeElement('searchPanel'));
    dispatch(actions.closeElement('redactionPanel'));
    const contentEditManager = core.getContentEditManager(activeDocumentViewerKey);
    if (contentEditManager.isInContentEditMode()) {
      dispatch(actions.closeElement('textEditingPanel'));
      contentEditManager.endContentEditMode();
    }

    dispatch(actions.triggerNoteEditing());
    if (isInlineCommentingDisabled) {
      dispatch(actions.openElement(DataElements.NOTES_PANEL));
      dispatch(actions.closeElement(DataElements.ANNOTATION_POPUP));
    } else {
      if (!isNotesPanelOpen) {
        dispatch(actions.openElement(DataElements.INLINE_COMMENT_POPUP));
      }
      closePopup();
    }
  };

  /* ALIGN */
  const showAlignButton = (
    canModify
    && multipleAnnotationsSelected
    && !isMobile()
  );

  const onOpenAlignmentModal = () => {
    dispatch(actions.openElement(DataElements.ANNOTATION_ALIGNMENT_POPUP));
    closePopup();
  };

  const handleDateChange = (text) => {
    annotManager.setNoteContents(focusedAnnotation, text);
    annotManager.updateAnnotation(focusedAnnotation);
  };

  const onDatePickerShow = (isDatePickerShowed) => {
    setDatePickerMount(isDatePickerShowed);
  };

  /* EDIT STYLE */
  const hasStyle = Object.keys(focusedAnnotationStyle).length > 0;

  const toolsWithNoStyling = [
    ToolNames.CROP,
    ToolNames.VIDEO_REDACTION,
    ToolNames.VIDEO_AND_AUDIO_REDACTION,
    ToolNames.AUDIO_REDACTION,
  ];

  const showEditStyleButton = (
    canModify
    && hasStyle
    && (!isAnnotationStylePopupDisabled || customizableUI)
    && (!multipleAnnotationsSelected || canUngroup || (multipleAnnotationsSelected && !isFocusedAnnotationSelected))
    && !toolsWithNoStyling.includes(focusedAnnotation.ToolName)
    && !(focusedAnnotation instanceof Annotations.Model3DAnnotation)
    && !focusedAnnotation.isContentEditPlaceholder()
    && !isAppearanceSignature
    && !(focusedAnnotation instanceof Annotations.PushButtonWidgetAnnotation)
  );

  const hideSnapModeCheckbox = focusedAnnotation instanceof Annotations.EllipseAnnotation || !core.isFullPDFEnabled();

  const onResize = () => {
    setStylePopupRepositionFlag(!stylePopupRepositionFlag);
  };

  /* EDIT CONTENT */
  const showContentEditButton =
    focusedAnnotation.isContentEditPlaceholder()
    && focusedAnnotation.getContentEditType() === window.Core.ContentEdit.Types.TEXT;

  const onEditContent = async () => {
    annotManager.trigger('annotationDoubleClicked', focusedAnnotation);

    dispatch(actions.closeElement(DataElements.ANNOTATION_POPUP));
  };

  const onClearAppearanceSignature = () => {
    focusedAnnotation.clearSignature(annotManager);
    closePopup();
  };

  /* REDACTION */
  const redactionEnabled = core.isAnnotationRedactable(focusedAnnotation, activeDocumentViewerKey);
  const showRedactionButton = redactionEnabled && !multipleAnnotationsSelected && !includesFormFieldAnnotation;

  const onApplyRedaction = () => {
    dispatch(applyRedactions(focusedAnnotation, undefined, activeDocumentViewerKey));
    dispatch(actions.closeElement(DataElements.ANNOTATION_POPUP));
  };

  /* GROUP / UNGROUP */
  const primaryAnnotation = selectedAnnotations.find((selectedAnnotation) => !selectedAnnotation.InReplyTo);

  const showGroupButton =
    canModify
    && isFocusedAnnotationSelected
    && canGroup
    && !includesFormFieldAnnotation;

  const onGroupAnnotations = () => {
    core.groupAnnotations(primaryAnnotation, selectedAnnotations, activeDocumentViewerKey);
  };

  const showUngroupButton = canUngroup;

  const onUngroupAnnotations = () => {
    core.ungroupAnnotations(selectedAnnotations, activeDocumentViewerKey);
  };

  /* FORM FIELD */
  const formFieldCreationManager = core.getFormFieldCreationManager(activeDocumentViewerKey);
  const isInFormFieldCreationMode = formFieldCreationManager.isInFormFieldCreationMode();
  const showFormFieldButton = includesFormFieldAnnotation
    && isInFormFieldCreationMode
    && !(focusedAnnotation instanceof Annotations.PushButtonWidgetAnnotation);

  const onOpenFormField = () => {
    closePopup();
    // We disable it while the form field popup is open to prevent having both open
    // at the same time. We re-enable it when the form field popup is closed.
    if (customizableUI) {
      dispatch(actions.disableElement(PRIORITY_THREE));
      dispatch(actions.closeElement(DataElements.FORM_FIELD_EDIT_POPUP));
    } else {
      dispatch(actions.disableElement(DataElements.ANNOTATION_POPUP, PRIORITY_TWO));
    }
    dispatch(actions.openElement(DataElements.FORM_FIELD_EDIT_POPUP));
    dispatch(actions.openElement(DataElements.FORM_FIELD_PANEL));
  };

  /* CLEAR APPEARANCE SIGNATURE */
  const showClearSignatureButton = canModify && isAppearanceSignature && !showFormFieldButton;
  /* DELETE ANNOTATION */
  const showDeleteButton = canModify && !showClearSignatureButton;

  const openContentEditDeleteWarningModal = () => {
    const message = t('option.contentEdit.deletionModal.message');
    const title = t('option.contentEdit.deletionModal.title');
    const confirmBtnText = t('action.ok');

    const warning = {
      message,
      title,
      confirmBtnText,
      onConfirm: () => onDeleteAnnotation(),
    };
    dispatch(actions.showWarningMessage(warning));
  };

  const onDeleteAnnotation = () => {
    if (isFocusedAnnotationSelected) {
      core.deleteAnnotations(core.getSelectedAnnotations(activeDocumentViewerKey), undefined, activeDocumentViewerKey);
    } else {
      core.deleteAnnotations([focusedAnnotation], undefined, activeDocumentViewerKey);
    }
    closePopup();
  };

  /* LINK ANNOTATION */
  const toolsThatCantHaveLinks = [
    ToolNames.CROP,
    ToolNames.SIGNATURE,
    ToolNames.REDACTION,
    ToolNames.REDACTION2,
    ToolNames.REDACTION3,
    ToolNames.REDACTION4,
    ToolNames.STICKY,
    ToolNames.STICKY2,
    ToolNames.STICKY3,
    ToolNames.STICKY4,
  ];

  const showLinkButton = (
    canModify
    && !toolsThatCantHaveLinks.includes(focusedAnnotation.ToolName)
    && !includesFormFieldAnnotation
    && !focusedAnnotation.isContentEditPlaceholder()
    // TODO(Adam): Update this once SoundAnnotation tool is created.
    && !(focusedAnnotation instanceof Annotations.SoundAnnotation)
    && !isAppearanceSignature
  );

  const showCalibrateButton =
    canModify
    && focusedAnnotation.Measure
    && focusedAnnotation instanceof Annotations.LineAnnotation;

  const onOpenCalibration = () => {
    dispatch(actions.closeElement(DataElements.ANNOTATION_POPUP));
    dispatch(actions.openElement(DataElements.CALIBRATION_MODAL));
  };

  const linkAnnotationToURL = () => {
    if (hasAssociatedLink) {
      const annotationsToUnlink = isFocusedAnnotationSelected ? selectedAnnotations : [focusedAnnotation];
      annotationsToUnlink.forEach((annot) => {
        const linkAnnotations = getGroupedLinkAnnotations(annot);
        linkAnnotations.forEach((linkAnnot, index) => {
          annotManager.ungroupAnnotations([linkAnnot]);
          if (annot instanceof Annotations.TextHighlightAnnotation && annot.Opacity === 0 && index === 0) {
            annotManager.deleteAnnotations([annot, linkAnnot], null, true);
          } else {
            annotManager.deleteAnnotation(linkAnnot, null, true);
          }
        });
      });
      closePopup();
    } else {
      dispatch(actions.openElement(DataElements.LINK_MODAL));
    }
  };

  /* DOWNLOAD FILE ATTACHMENT */
  const showFileDownloadButton = focusedAnnotation instanceof Annotations.FileAttachmentAnnotation;

  const downloadFileAttachment = async (annot) => {
    // no need to check that annot is of type file annot as the check is done in the JSX
    const { fileData, fileName } = await annot.getFullFileMetadata();
    saveAs(fileData, fileName);
  };

  /* AUDIO ANNOTATION */
  const showAudioPlayButton = (
    !isIE &&
    !selectedMultipleAnnotations &&
    focusedAnnotation instanceof Annotations.SoundAnnotation &&
    focusedAnnotation.hasAudioData()
  );

  const isNotesPanelOpenOrActive = isNotesPanelOpen
    || (notesInLeftPanel && leftPanelOpen && activeLeftPanel === 'notesPanel')
    || isAnyCustomPanelOpen
    || isInFormFieldCreationMode;

  const handlePlaySound = (annotation) => {
    dispatch(actions.setActiveSoundAnnotation(annotation));
    dispatch(actions.triggerResetAudioPlaybackPosition(true));
    dispatch(actions.openElement('audioPlaybackPopup'));
  };

  return (
    <AnnotationPopup
      isMobile={isMobile()}
      isIE={isIE}
      isOpen={isOpen}
      isRightClickMenu={isRightClickAnnotationPopupEnabled}
      isNotesPanelOpenOrActive={isNotesPanelOpenOrActive}
      isRichTextPopupOpen={isRichTextPopupOpen}
      isLinkModalOpen={isLinkModalOpen}
      isWarningModalOpen={isWarningModalOpen}
      isContextMenuPopupOpen={isContextMenuPopupOpen}
      isVisible={isVisible}

      popupRef={popupRef}
      position={position}
      focusedAnnotation={focusedAnnotation}
      multipleAnnotationsSelected={multipleAnnotationsSelected}

      showViewFileButton={showViewFileButton}
      onViewFile={onViewFile}

      showCommentButton={showCommentButton}
      onCommentAnnotation={onCommentAnnotation}
      isDateFreeTextCanEdit={isDateFreeTextCanEdit}
      isDatePickerOpen={isDatePickerOpen}
      handleDateChange={handleDateChange}
      onDatePickerShow={onDatePickerShow}
      isCalibrationPopupOpen={isCalibrationPopupOpen}

      showEditStyleButton={showEditStyleButton}
      isStylePopupOpen={isStylePopupOpen}
      hideSnapModeCheckbox={hideSnapModeCheckbox}
      openEditStylePopup={() => setIsStylePopupOpen(true)}
      closeEditStylePopup={() => setIsStylePopupOpen(false)}
      annotationStyle={focusedAnnotationStyle}
      onResize={onResize}

      showContentEditButton={showContentEditButton}
      onEditContent={onEditContent}
      openContentEditDeleteWarningModal={openContentEditDeleteWarningModal}

      showClearSignatureButton={showClearSignatureButton}
      onClearAppearanceSignature={onClearAppearanceSignature}

      showRedactionButton={showRedactionButton}
      onApplyRedaction={onApplyRedaction}

      showGroupButton={showGroupButton}
      onGroupAnnotations={onGroupAnnotations}
      showUngroupButton={showUngroupButton}
      onUngroupAnnotations={onUngroupAnnotations}

      showFormFieldButton={showFormFieldButton}
      onOpenFormField={onOpenFormField}

      showDeleteButton={showDeleteButton}
      onDeleteAnnotation={onDeleteAnnotation}

      showLinkButton={showLinkButton}
      hasAssociatedLink={hasAssociatedLink}
      linkAnnotationToURL={linkAnnotationToURL}

      showFileDownloadButton={showFileDownloadButton}
      downloadFileAttachment={downloadFileAttachment}

      showAudioPlayButton={showAudioPlayButton}
      handlePlaySound={handlePlaySound}

      showCalibrateButton={showCalibrateButton}
      onOpenCalibration={onOpenCalibration}

      customizableUI={customizableUI}
      toggleStylePanel={toggleStylePanel}
      isStylePanelOpen={isStylePanelOpen}
      isInReadOnlyMode={isInReadOnlyMode}

      showAlignButton={showAlignButton}
      onOpenAlignmentModal={onOpenAlignmentModal}
    />
  );
};

AnnotationPopupContainer.propTypes = propTypes;

export default AnnotationPopupContainer;
